import { useV2TermsControllerFetchTermsVersions, type V2TermsVersionListResponseDto, V2TermsControllerFetchTermsVersionsCategory as TermsCategory} from "@everon-it/api-client/contents-gw";
import { ICONS } from "@assets/icons";
import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import IconBox from "@components/atoms/IconBox";
import Table from "@components/atoms/Table";
import { PrivateRoutes } from "@constants/urls";
import useGlobalModal from "@hooks/useGlobalModal";
import useRouter from "@hooks/useRouter";
import useTermsDataStore from "@store/terms";
import { colors, typo } from "@styles/index";
import { RenderCommonElementByType } from "@utils/DynamicComponentUtils";
import { toEnumValue } from "@utils/typeGuards";
import { DateUtil } from "@everon-it/core";
import { Pagination } from "antd";
import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { TermsDetailData } from "./Data";

const TermsDetail: React.FC = () => {
    // Props
    const HEADER_TITLE = "약관 상세";
    const CONTENT_TITLE = "약관 정보";
    const SUB_CONTENT_TITLE = "약관 이력";
    const GIRD_COLUMN = 2;
    const TABLE_BUTTON_TEXT = "약관 이력 등록";

    // Data
    const { formInitialValues, forms: contentsOption, tableList: tableHeaderTitleData } = TermsDetailData;

    // Hooks
    const { query,push } = useRouter();
    const { showTermsEditorModal } = useGlobalModal();
    const { categoryInfo } = useTermsDataStore(state => state);
    const [searchParams, setSearchParams] = useSearchParams();

    // URL 파라미터에서 category를 타입 안전하게 추출
    const category = toEnumValue(String(query.category), TermsCategory);
    const pageParam = searchParams.get("page");
    const pageSizeParam = searchParams.get("pageSize");
    const [page, setPage] = useState<number>(pageParam ? Number(pageParam) : 1);
    const [pageSize, setPageSize] = useState<number>(pageSizeParam ? Number(pageSizeParam) : 10);

    // Formik
    const formik = useFormik({
        initialValues: formInitialValues,
        onSubmit: () => {
            // 읽기 전용 페이지 - 폼 제출 불필요
        },
    });

    // api-client: 약관 버전 목록 조회
    const { data } = useV2TermsControllerFetchTermsVersions(
        {
            category,
            page,
            pageSize,
        },
        {
            query: {
                enabled: !!category && searchParams.toString().includes("page"),
                retry: false,
                refetchOnWindowFocus: false,
            },
        },
    );

    const termVersionsList: V2TermsVersionListResponseDto[] = data?.rows ?? [];
    const totalCount = data?.count ?? 0;

    // Store의 categoryInfo로 약관 정보 표시
    useEffect(() => {
        if (categoryInfo.title) {
            void formik.setFieldValue("title", categoryInfo.title);
            void formik.setFieldValue("isRequired", categoryInfo.isRequired ? "필수" : "선택");
            void formik.setFieldValue("isVisibleOnSignup", categoryInfo.isVisibleOnSignup ? "노출" : "미노출");
            void formik.setFieldValue("isVisibleOnServicePage", categoryInfo.isVisibleOnServicePage ? "노출" : "미노출");
        }
    }, [categoryInfo]);

    /** 약관 버전 리스트 */
    const termHistoriesList = termVersionsList.map(item => {
        const { effectiveStartAt, effectiveEndAt, ...rest } = item;
        const today = moment().utc();
        const startDate = moment(effectiveStartAt).utc();
        const endDate = effectiveEndAt ? moment(effectiveEndAt).utc() : null;
        const isPosting = endDate ? (today >= startDate && today <= endDate ? "✅" : "") : today >= startDate ? "✅" : "";
        return {
            ...rest,
            effectiveDataAt: `${DateUtil.toDateString(effectiveStartAt)} ~ ${effectiveEndAt ? DateUtil.toDateString(effectiveEndAt) : ""} ${isPosting}`,
        };
    });

    const handleGoBackClick = () => {
        push(PrivateRoutes.Terms);
    };

    // 약관 이력 등록 모달 열기
    const handleTermsEditorOpenModal = () => {
        showTermsEditorModal({
            title: categoryInfo.title ?? "",
        });
    };

    const handleChangePageSize = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
    };

    useEffect(() => {
        searchParams.set("page", String(page));
        searchParams.set("pageSize", String(pageSize));

        setSearchParams(searchParams);
    }, [page, pageSize, searchParams, setSearchParams]);

    return (
        <ContentsWrap>
            <HeaderWrap>
                <IconBox width={32} height={32} src={ICONS.ARROW_ICON.BACK} isCursorPointer onClick={handleGoBackClick} />
                <HeaderTitle>{HEADER_TITLE}</HeaderTitle>
            </HeaderWrap>
            <div>
                <InnerContentsWrap>
                    <ContentBox style={{ padding: 0 }}>
                        <StyledContentTitle>{CONTENT_TITLE}</StyledContentTitle>
                        <InfoBoxWrap>
                            <InfoBox girdColumn={GIRD_COLUMN}>
                                {contentsOption.map(({ label, ...res }) => {
                                    return (
                                        <CustomInputWrap key={label}>
                                            <CustomLabel>{label}</CustomLabel>
                                            {RenderCommonElementByType({
                                                formik,
                                                ...res,
                                            })}
                                        </CustomInputWrap>
                                    );
                                })}
                            </InfoBox>
                        </InfoBoxWrap>
                    </ContentBox>
                    <ContentBox style={{ padding: 0 }}>
                        <TableTitleWrap>
                            {SUB_CONTENT_TITLE} <Button buttonSize="small" text={TABLE_BUTTON_TEXT} onClick={handleTermsEditorOpenModal} />
                        </TableTitleWrap>
                        <TableWrap>
                            <Table tableContentsInfoData={tableHeaderTitleData} rowData={termHistoriesList} currentPageSize={pageSize} minHeight={300} />
                            <TableBottomWrap>
                                <ContentsTotal>
                                    총<span>{totalCount}</span>
                                </ContentsTotal>
                                <PageNationWrap>
                                    <Pagination
                                        defaultCurrent={page}
                                        defaultPageSize={pageSize}
                                        total={totalCount}
                                        showSizeChanger
                                        locale={{ items_per_page: "개씩 보기" }}
                                        current={page}
                                        pageSize={pageSize}
                                        onShowSizeChange={handleChangePageSize}
                                        onChange={setPage}
                                    />
                                </PageNationWrap>
                            </TableBottomWrap>
                        </TableWrap>
                    </ContentBox>
                </InnerContentsWrap>
            </div>
        </ContentsWrap>
    );
};

export default TermsDetail;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const HeaderWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

const HeaderTitle = styled(MainHeaderTitle)`
    margin: 0;
`;

const TableTitle = styled.p`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const InnerContentsWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const ContentTitle = styled(TableTitle)`
    margin: 0;
    padding: 24px;
    border-bottom: 1px solid ${colors.GRAY9};
`;

const InfoBoxWrap = styled.div`
    padding: 40px;
`;

const CustomInputWrap = styled.div<{ flex?: number; minWidth?: string }>`
    width: 100%;
    display: flex;
    align-items: center;
    border-bottom: 1px solid ${colors.GRAY9};
`;

const CustomLabel = styled.label`
    min-width: 130px;
    padding: 12px;
    background-color: ${colors.GRAY11};
    color: ${colors.GRAY4};
    ${typo.BODY_8};
`;

const StyledContentTitle = styled(ContentTitle)`
    padding: 24px;
    border-bottom: 1px solid ${colors.GRAY9};
`;

const FilterBox = styled.div<{ girdColumn?: number }>`
    width: 100%;
    margin-bottom: 12px;
    border-top: 1px solid ${colors.GRAY9};

    display: grid;
    grid-template-columns: repeat(${({ girdColumn }) => girdColumn}, 1fr);

    .faqTitle {
        grid-column: 1 / 3;
    }
`;

const InfoBox = styled(FilterBox)`
    margin-bottom: unset;
`;

const ContentsMainWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 840px;
    margin: auto;
    padding: 52px 0 80px;
`;

const TableWrap = styled(ContentsMainWrap)`
    /*  */
    padding: 40px 40px 80px;
    margin: unset;
    max-width: unset;
`;

const TableTitleWrap = styled(ContentTitle)`
    display: flex;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid ${colors.GRAY9};
`;

const TableBottomWrap = styled.div`
    display: flex;

    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
`;

const ContentsTotal = styled.span`
    display: flex;
    gap: 8px;
    color: ${colors.GRAY6};
    font-weight: 500;
    ${typo.BODY_8};

    span {
        ${typo.BODY_8};
        font-weight: 500;
        color: ${colors.GRAY2};
    }
`;

const PageNationWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;
